import { amplifyProxy } from '@/services/directApi';

const state = {
  loading: false,
  error: null,
  submitting: false,
  createdContractorId: null,
  contractorTypes: [],
  contractorTypesLoading: false
};

const mutations = {
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_SUBMITTING(state, value) {
    state.submitting = value;
  },
  SET_CREATED_CONTRACTOR_ID(state, id) {
    state.createdContractorId = id;
  },
  RESET_STATE(state) {
    state.loading = false;
    state.error = null;
    state.submitting = false;
    state.createdContractorId = null;
    state.contractorTypes = [];
    state.contractorTypesLoading = false;
  },
  SET_CONTRACTOR_TYPES(state, types) {
    state.contractorTypes = types;
  },
  SET_CONTRACTOR_TYPES_LOADING(state, value) {
    state.contractorTypesLoading = value;
  }
};

const actions = {
  async createContractor({ commit, rootState }, contractorData) {
    commit("SET_SUBMITTING", true);
    commit("SET_ERROR", null);
    
    try {
      const endpoint = `/${rootState.user.ClientName}/contractors`;
      const body = contractorData;
      const options = {
        headers: {
          token: rootState.user.LoginToken
        }
      };

      const response = await amplifyProxy.post(endpoint, body, options);
      
      // The Lambda proxy returns { data: actualResponse, status: '200', ... }
      // Check various possible response structures
      let contractorId = null;
      
      // First check if we have response.data (from Lambda proxy)
      if (response && response.data !== undefined && response.data !== null) {
        // Check if data contains contractorID property
        if (response.data.contractorID) {
          contractorId = response.data.contractorID;
        } else if (response.data.ContractorID) {
          contractorId = response.data.ContractorID;
        } else if (typeof response.data === 'string' || typeof response.data === 'number') {
          contractorId = response.data;
        }
      } 
      // If no data property, check if response itself is the ID
      else if (response && (typeof response === 'string' || typeof response === 'number')) {
        contractorId = response;
      }
      // Check if response has other common ID properties
      else if (response && response.id) {
        contractorId = response.id;
      }
      else if (response && response.ID) {
        contractorId = response.ID;
      }
      else if (response && response.OID) {
        contractorId = response.OID;
      }
      
      // If we found an ID, use it
      if (contractorId !== null && contractorId !== undefined && contractorId !== "") {
        commit("SET_CREATED_CONTRACTOR_ID", String(contractorId));
        return contractorId;
      } else {
        // If no ID found but we have a successful status, the API might be failing silently
        if (response && response.status === "200") {
          throw new Error("API returned success status but no contractor was created. Please check the API logs or validate the request data.");
        } else {
          throw new Error("No contractor ID in response");
        }
      }
    } catch (error) {
      commit("SET_ERROR", error.message || "Failed to create contractor");
      throw error;
    } finally {
      commit("SET_SUBMITTING", false);
    }
  },

  async searchContractors({ rootState }, searchTerm) {
    if (!searchTerm || searchTerm.length < 3) {
      return [];
    }

    try {
      const endpoint = `/${rootState.user.ClientName}/contractors/all/records`;
      const options = {
        headers: {
          token: rootState.user.LoginToken
        },
        queryStringParameters: {
          search: searchTerm
        }
      };

      const response = await amplifyProxy.get(endpoint, options);
      
      if (response && response.data) {
        const items = response.data.Data || response.data.items || response.data || [];
        return items.map(contractor => ({
          OID: contractor.OID || contractor.id,
          Subject: contractor.Name || contractor.name || `${contractor.Forename || contractor.forename} ${contractor.Surname || contractor.surname}`,
          CompanyName: contractor.CompanyName || contractor.companyName,
          Email: contractor.Email || contractor.email,
          MobilePhone: contractor.MobilePhone || contractor.mobilePhone
        }));
      }
      
      return [];
    } catch (error) {
      return [];
    }
  },

  async fetchContractorTypes({ commit, rootState }) {
    commit("SET_CONTRACTOR_TYPES_LOADING", true);
    
    try {
      const endpoint = `/${rootState.user.ClientName}/contractors/types`;
      const options = {
        headers: {
          token: rootState.user.LoginToken
        }
      };

      const response = await amplifyProxy.get(endpoint, options);
      
      if (response && response.data) {
        const types = Array.isArray(response.data) ? response.data : response.data.Data || [];
        commit("SET_CONTRACTOR_TYPES", types);
        return types;
      }
      
      commit("SET_CONTRACTOR_TYPES", []);
      return [];
    } catch (error) {
      console.error("Error fetching contractor types:", error);
      commit("SET_CONTRACTOR_TYPES", []);
      return [];
    } finally {
      commit("SET_CONTRACTOR_TYPES_LOADING", false);
    }
  },

  resetContractorManagement({ commit }) {
    commit("RESET_STATE");
  }
};

const getters = {
  isLoading: state => state.loading,
  isSubmitting: state => state.submitting,
  error: state => state.error,
  createdContractorId: state => state.createdContractorId,
  contractorTypes: state => state.contractorTypes,
  contractorTypesLoading: state => state.contractorTypesLoading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};